import React from 'react';

import { Image, Link, Scrollbar } from 'components/ui';
import { isArrayEmpty } from 'helpers/ArrayHelpers';
import { parseCategoryImageUrl } from 'helpers/CategoryHelpers';
import { getCategoryFilter } from 'helpers/FiltersHelpers';
import { cx } from 'helpers/StylingHelpers';
import { useInView } from 'hooks/common/useInView';
import { useTranslation } from 'hooks/common/useTranslation';
import { useAnalytics } from 'hooks/useAnalytics';
import { useFilters } from 'hooks/useFilters';

import { PageType } from 'types';
import type {
  IFilter, IFilterOption, IFilters, IUrl
} from 'types';

import styles from './SearchPageCategoryFilterDesktop.module.scss';

interface Props {
  filters: IFilters
}

const SearchPageCategoryFilterDesktop = (props: Props) => {
  const { filters } = props;

  const { t } = useTranslation();
  const { getFilterHref } = useFilters({ pageType: PageType.Search, items: filters.items });

  const categoryFilter = getCategoryFilter(filters);

  if (!categoryFilter || isArrayEmpty(categoryFilter.values)) {
    return null;
  }

  const options = categoryFilter.values;

  return (
    <div className={styles.root}>
      <div className={styles.title}>
        {t('SEARCH.SUGGESTED_CATEGORIES_TITLE')}
      </div>
      <Scrollbar
        className={styles.scrollbar}
        autoHide={false}
        forceVisible="y"
        draggable
      >
        <div className={styles.options}>
          {
            options?.map((option, index) => {
              const { code } = option || {};
              return (
                <CategoryFilterOption
                  href={getFilterHref(option, categoryFilter)}
                  key={`${code}-${index}`}
                  option={option}
                  parent={categoryFilter}
                />
              );
            })
          }
        </div>
      </Scrollbar>
    </div>
  );
};

interface CategoryFilterOptionProps {
  option: IFilterOption,
  parent: IFilter,
  href: IUrl
}

const CategoryFilterOption = (props: CategoryFilterOptionProps) => {
  const { option, href, parent } = props;
  const {
    name, image, isSelected, count
  } = option || {};

  const analytics = useAnalytics();
  const handleClick = () => {
    if (image) {
      analytics.selectProductCategoryItem(parseCategoryImageUrl(image));
    }
    if (!isSelected) {
      analytics.applyFilter(parent?.name, option?.name);
    } else {
      analytics.removeFilter(parent?.name, option?.name);
    }
  };

  const { observe } = useInView({
    threshold: 0.75,
    onEnter: ({ unobserve }) => {
      if (image) {
        analytics.viewProductCategoryItem(parseCategoryImageUrl(image));
      }
      unobserve();
    }
  });

  const countLabel = `(${count})`;

  return (
    <div
      ref={observe}
      className={cx(styles.optionWrapper, { [styles.active]: isSelected })}
      onClick={handleClick}
    >
      <Link passHref href={href.toString()} prefetch={false}>
        <a className={cx(styles.option)}>
          {image ? <Image className={styles.image} src={image} width={40} height={40} /> : null}
          <div>
            {name}
            <span className={styles.count}>{countLabel}</span>
          </div>
        </a>
      </Link>
    </div>
  );
};

export default SearchPageCategoryFilterDesktop;
